/**
 * Housekeeping: tag management, and purging tracks whose files have gone.
 *
 * Purge is the only thing in this app that destroys work — the index row, the
 * analysis and the peaks, none of which come back without re-analysing on two
 * shared cores. So the UI here is deliberately a two-step: you get the list
 * first, and the confirm button carries the count you were shown.
 */

import {
  deleteTag,
  fetchPurgePlan,
  fetchTags,
  renameTag,
  runPurge,
  type PurgePlan,
  type Tag,
} from "../api.js";
import { escapeHtml } from "./format.js";

const MAX_LISTED = 40;

export class CurationPanel {
  private plan: PurgePlan | null = null;
  private tags: Tag[] = [];
  private busy = false;

  constructor(
    private readonly root: HTMLElement,
    /** Called after anything that changes the library, so the list can reload. */
    private readonly onChanged: () => void,
  ) {
    this.root.innerHTML = `
      <h2>Housekeeping</h2>
      <div class="curation-section">
        <h3>Tags</h3>
        <div id="cur-tags" class="tag-admin"></div>
      </div>
      <div class="curation-section">
        <h3>Purge missing tracks</h3>
        <p class="muted">
          Removes index entries for files that are no longer on disk, and their
          analysis. It never deletes audio — it cannot, the library is read-only.
        </p>
        <div class="curation-actions">
          <button id="cur-plan" type="button">Check what is missing</button>
          <button id="cur-apply" type="button" class="danger" hidden></button>
        </div>
        <div id="cur-purge" class="curation-result"></div>
      </div>
    `;
    this.bind();
  }

  private $(id: string): HTMLElement {
    const node = this.root.querySelector(`#${id}`);
    if (!node) throw new Error(`missing #${id}`);
    return node as HTMLElement;
  }

  private bind(): void {
    this.$("cur-plan").addEventListener("click", () => void this.check());
    this.$("cur-apply").addEventListener("click", () => void this.apply());

    this.$("cur-tags").addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      const del = target.closest<HTMLElement>("button[data-delete-tag]");
      if (del) {
        void this.removeTag(Number(del.dataset["deleteTag"]), del.dataset["name"] ?? "");
        return;
      }
      const ren = target.closest<HTMLElement>("button[data-rename-tag]");
      if (ren) void this.rename(Number(ren.dataset["renameTag"]), ren.dataset["name"] ?? "");
    });
  }

  async refresh(): Promise<void> {
    this.tags = await fetchTags();
    this.renderTags();
  }

  private renderTags(): void {
    if (this.tags.length === 0) {
      this.$("cur-tags").innerHTML =
        `<p class="muted">No tags yet. Double-click a track's Tags cell to add one.</p>`;
      return;
    }
    this.$("cur-tags").innerHTML = this.tags
      .map(
        (t) =>
          `<span class="tag-chip">${escapeHtml(t.name)} <span class="muted">${t.track_count}</span>` +
          `<button type="button" data-rename-tag="${t.id}" data-name="${escapeHtml(t.name)}" title="Rename">✎</button>` +
          `<button type="button" data-delete-tag="${t.id}" data-name="${escapeHtml(t.name)}" title="Delete this tag (tracks are kept)">×</button>` +
          `</span>`,
      )
      .join(" ");
  }

  private async rename(tagId: number, current: string): Promise<void> {
    const next = window.prompt(`Rename “${current}” to:`, current);
    if (next === null || next.trim() === "" || next === current) return;
    try {
      await renameTag(tagId, next);
      await this.refresh();
      this.onChanged();
    } catch (err) {
      this.$("cur-purge").innerHTML = `<span class="bad">${escapeHtml(String(err))}</span>`;
    }
  }

  private async removeTag(tagId: number, name: string): Promise<void> {
    // Worth a confirm: deleting a tag is not destructive to audio, but it does
    // silently unfile every track that carried it.
    if (!window.confirm(`Delete the tag “${name}”? The tracks themselves are kept.`)) return;
    try {
      await deleteTag(tagId);
      await this.refresh();
      this.onChanged();
    } catch (err) {
      this.$("cur-purge").innerHTML = `<span class="bad">${escapeHtml(String(err))}</span>`;
    }
  }

  /** Dry run. Reads only — this is what the confirm button is then bound to. */
  private async check(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    const out = this.$("cur-purge");
    const apply = this.$("cur-apply") as HTMLButtonElement;
    apply.hidden = true;
    out.textContent = "Checking…";

    try {
      this.plan = await fetchPurgePlan();
      out.innerHTML = this.describe(this.plan);
      if (this.plan.count > 0) {
        apply.hidden = false;
        apply.textContent = `Purge ${this.plan.count} track${this.plan.count === 1 ? "" : "s"}`;
      }
    } catch (err) {
      // A 409 here is the safety guard talking, not a fault. Its message says
      // which root is unready or which is over the threshold, so it is shown
      // verbatim rather than summarised.
      this.plan = null;
      out.innerHTML = `<span class="warn">${escapeHtml(String(err))}</span>`;
    } finally {
      this.busy = false;
    }
  }

  private describe(plan: PurgePlan): string {
    if (plan.count === 0) return `<span class="muted">Nothing is missing. Nothing to purge.</span>`;

    const roots = Object.entries(plan.per_root)
      .filter(([, s]) => s.missing > 0)
      .map(
        ([root, s]) =>
          `<li><b>${escapeHtml(root)}</b>: ${s.missing} of ${s.total} (${Math.round(s.share * 100)}%)</li>`,
      )
      .join("");

    const listed = plan.tracks
      .slice(0, MAX_LISTED)
      .map((t) => `<li>${escapeHtml(t.root)}/${escapeHtml(t.path)}</li>`)
      .join("");
    const more =
      plan.tracks.length > MAX_LISTED
        ? `<li class="muted">…and ${plan.tracks.length - MAX_LISTED} more</li>`
        : "";

    return `
      <p class="warn">${plan.count} track${plan.count === 1 ? "" : "s"} would be permanently
      removed from the index, along with their analysis.</p>
      <ul>${roots}</ul>
      <ul class="purge-list">${listed}${more}</ul>
    `;
  }

  private async apply(): Promise<void> {
    if (this.busy || !this.plan) return;
    const count = this.plan.count;
    if (
      !window.confirm(
        `Permanently remove ${count} track${count === 1 ? "" : "s"} from the index?\n\n` +
          `Their analysis and waveforms go too, and re-analysing takes a while. ` +
          `Audio files are not touched.`,
      )
    ) {
      return;
    }

    this.busy = true;
    const out = this.$("cur-purge");
    out.textContent = "Purging…";
    try {
      // The count is sent back so the server can refuse if the library changed
      // between the dry run and this click.
      const result = await runPurge(count);
      out.innerHTML =
        `<span class="muted">Purged ${result.purged} track(s); ` +
        `removed ${result.peaks_removed} waveform file(s)` +
        (result.peaks_failed ? `, ${result.peaks_failed} could not be removed` : "") +
        `.</span>`;
      this.plan = null;
      (this.$("cur-apply") as HTMLButtonElement).hidden = true;
      await this.refresh();
      this.onChanged();
    } catch (err) {
      out.innerHTML = `<span class="bad">${escapeHtml(String(err))}</span>`;
    } finally {
      this.busy = false;
    }
  }
}
